import { createTheme, type Theme } from '@mui/material/styles';
import { branding } from './branding';

/**
 * Corporate, quiet, mobile-first.
 *
 * The brand asks for sharp corners, bold uppercase headings and purple used as
 * an accent on white - never as a background flood - so the surfaces stay
 * white and the colour appears on actions, status and focus. Signing a
 * document has to feel calm on a phone, which is why type scales down
 * gracefully and touch targets stay generous.
 */
export function buildTheme(): Theme {
  const theme = createTheme({
    cssVariables: true,
    palette: {
      mode: 'light',
      primary: { main: branding.primaryColor, contrastText: '#ffffff' },
      secondary: { main: branding.secondaryColor, contrastText: '#ffffff' },
      warning: { main: '#fb7a00' },
      success: { main: '#167333' },
      error: { main: '#c0271f' },
      background: { default: '#f6f5f8', paper: '#ffffff' },
      text: { primary: '#1a1a1a', secondary: '#5c5c66' },
      divider: '#e3e1e8',
    },

    shape: { borderRadius: 4 },

    typography: {
      fontFamily: "'Inter', 'GT America', 'Helvetica Neue', Arial, sans-serif",
      h1: { fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.45rem', fontWeight: 700, letterSpacing: '-0.015em' },
      h3: { fontSize: '1.2rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h4: { fontSize: '1.05rem', fontWeight: 600 },
      h5: { fontSize: '0.95rem', fontWeight: 600 },
      h6: { fontSize: '0.875rem', fontWeight: 600 },
      subtitle2: { fontWeight: 600, letterSpacing: '0.02em' },
      button: { fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' },
      overline: { fontWeight: 700, letterSpacing: '0.12em' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased' },
          // Signature pads and PDF pages must not be dragged around as images.
          'canvas, .pdf-page': { userSelect: 'none' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 42, paddingInline: 20 },
          sizeLarge: { minHeight: 50, fontSize: '0.95rem' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: '#e3e1e8' },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 4, fontWeight: 600 },
          sizeSmall: { height: 22, fontSize: '0.72rem' },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small', fullWidth: true },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 700, whiteSpace: 'nowrap' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, minHeight: 48 },
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 4 } },
      },
    },
  });

  return theme;
}
